// Iter50 — Standalone "+ New engineering request" modal for /engineering.
//
// Mirrors the sorting NewSortingRequestModal pattern: a single modal that
// creates an engineering service-request either standalone (no incident
// link) or linked to an existing open incident via a searchable picker.
// Posts to `POST /api/service-requests` with `type='engineering'`.
//
// Auto-opens when the user lands on `/engineering?new=1`.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useStore } from "../store";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const FieldLabel = ({ children }) => (
  <div className="uppercase mb-1.5" style={mono(9, "var(--dimmer, #9198b0)", 600, "0.8px")}>
    {children}
  </div>
);

const PRIORITIES = ["low", "medium", "high"];

export const NewEngineeringRequestModal = ({ onClose, onCreated }) => {
  const addToast = useStore((s) => s.addToast);
  const [linkMode, setLinkMode] = useState("standalone");
  const [linkedIncidentId, setLinkedIncidentId] = useState("");
  const [incidentSearch, setIncidentSearch] = useState("");
  const [openIncidents, setOpenIncidents] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/incidents?status_in=open,investigating,escalated&limit=200`)
      .then((r) => { if (!cancelled) setOpenIncidents(r.data || []); })
      .catch(() => { /* swallow */ });
    return () => { cancelled = true; };
  }, []);

  const filteredIncidents = useMemo(() => {
    const q = incidentSearch.trim().toLowerCase();
    if (!q) return openIncidents.slice(0, 25);
    return openIncidents
      .filter((i) => (i.id + " " + (i.title || "")).toLowerCase().includes(q))
      .slice(0, 25);
  }, [openIncidents, incidentSearch]);

  const submit = async () => {
    setErr(null);
    if (!title.trim()) { setErr("Title is required."); return; }
    if (linkMode === "incident" && !linkedIncidentId) {
      setErr("Pick an incident or switch to standalone.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        type: "engineering",
        title: title.trim(),
        description: description.trim() || null,
        priority,
      };
      if (linkMode === "incident") body.incident_id = linkedIncidentId;
      const r = await axios.post(`${API}/service-requests`, body);
      addToast({ type: "success", title: "Engineering request created", body: r.data?.id || "" });
      onCreated(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setErr(String(detail));
      addToast({ type: "error", title: "Failed to create engineering request", body: String(detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.65)",
        zIndex: 500,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      data-testid="engineering-create-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-surface, #0d0f18)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 8,
          width: "min(640px, 96vw)",
          maxHeight: "92vh",
          overflow: "auto",
          boxShadow: "0 18px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div>
            <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 18, color: "var(--text, #f0f2f8)" }}>
              New engineering request
            </div>
            <div className="mt-0.5" style={mono(10, "var(--dimmer, #9198b0)")}>
              Standalone or linked to an existing incident
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: "var(--dimmer, #9198b0)", fontSize: 22, cursor: "pointer" }}
            data-testid="engineering-create-close"
          >×</button>
        </div>

        <div className="px-5 py-4 space-y-5">
          <div data-testid="engineering-incident-link-section">
            <FieldLabel>Link to incident</FieldLabel>
            <div className="flex gap-2 mb-2 flex-wrap">
              {[
                { v: "standalone", label: "Standalone (no incident)" },
                { v: "incident", label: "Link to incident" },
              ].map((m) => (
                <button
                  type="button"
                  key={m.v}
                  onClick={() => setLinkMode(m.v)}
                  className="px-3 py-1.5 rounded"
                  style={{
                    background: linkMode === m.v ? "rgba(24,95,165,0.18)" : "transparent",
                    border: `1px solid ${linkMode === m.v ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                    color: linkMode === m.v ? "#9ec3ec" : "var(--text, #c5cad8)",
                    ...mono(10, undefined, 700, "0.4px"),
                    cursor: "pointer",
                  }}
                  data-testid={`engineering-link-mode-${m.v}`}
                >{m.label}</button>
              ))}
            </div>
            {linkMode === "incident" && (
              <>
                <input
                  type="search"
                  placeholder="Search incidents by id or title…"
                  value={incidentSearch}
                  onChange={(e) => setIncidentSearch(e.target.value)}
                  className="w-full px-2 py-1.5 rounded outline-none mb-1.5"
                  style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }}
                  data-testid="engineering-incident-search"
                />
                <div
                  className="rounded-md max-h-40 overflow-y-auto"
                  style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)" }}
                  data-testid="engineering-incident-picker-list"
                >
                  {filteredIncidents.length === 0 ? (
                    <div className="px-3 py-2" style={mono(11, "var(--dimmer, #9198b0)")}>No open incidents matched.</div>
                  ) : filteredIncidents.map((i) => (
                    <label
                      key={i.id}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                      style={{
                        borderBottom: "1px solid rgba(255,255,255,0.04)",
                        background: linkedIncidentId === i.id ? "rgba(24,95,165,0.10)" : "transparent",
                      }}
                    >
                      <input
                        type="radio"
                        name="engineering-link-incident"
                        checked={linkedIncidentId === i.id}
                        onChange={() => setLinkedIncidentId(i.id)}
                        data-testid={`engineering-pick-incident-${i.id}`}
                      />
                      <span style={mono(11, "var(--text, #f0f2f8)", 600)}>{i.id}</span>
                      <span style={mono(10, "var(--dimmer, #9198b0)")} className="truncate">{i.title || ""}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <div>
            <FieldLabel>Title *</FieldLabel>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Investigate fixture wear on left-hand bracket"
              className="w-full px-2 py-1.5 rounded outline-none"
              style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(12) }}
              data-testid="engineering-create-title"
            />
          </div>
          <div>
            <FieldLabel>Description / scope</FieldLabel>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Optional context — part number, observed deviation, expected output…"
              className="w-full px-2 py-1.5 rounded outline-none"
              style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11), resize: "vertical" }}
              data-testid="engineering-create-description"
            />
          </div>
          <div>
            <FieldLabel>Priority</FieldLabel>
            <div className="flex flex-wrap gap-1.5">
              {PRIORITIES.map((p) => (
                <button
                  type="button"
                  key={p}
                  onClick={() => setPriority(p)}
                  className="px-2.5 py-1 rounded"
                  style={{
                    background: priority === p ? "rgba(24,95,165,0.18)" : "transparent",
                    border: `1px solid ${priority === p ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                    color: priority === p ? "#9ec3ec" : "var(--text, #c5cad8)",
                    ...mono(10, undefined, 700, "0.4px"),
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                  data-testid={`engineering-create-priority-${p}`}
                >{p}</button>
              ))}
            </div>
          </div>

          {err && (
            <div
              className="rounded px-3 py-2"
              style={{ background: "rgba(226,75,74,0.12)", border: "1px solid rgba(226,75,74,0.4)", color: "#ff9a99", ...mono(11) }}
              data-testid="engineering-create-error"
            >{err}</div>
          )}
        </div>

        <div className="px-5 py-3 flex justify-end gap-2"
          style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "var(--text, #c5cad8)", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
            data-testid="engineering-create-cancel"
          >Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="px-3 py-1.5 rounded"
            style={{
              background: "#185FA5",
              color: "#fff",
              ...mono(10, undefined, 700, "0.4px"),
              border: "none",
              cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
            data-testid="engineering-create-submit"
          >{busy ? "Creating…" : "Create request"}</button>
        </div>
      </div>
    </div>
  );
};
